import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React, { useState, useRef, useEffect } from "react";
import { Mic, Volume2, MicOff, VolumeOff } from "lucide-react";
import { Tooltip } from "primereact/tooltip";
import { useAudioStore } from "@/shared/zustand/audioStore";
import { v4 as uuidv4 } from "uuid";
import { transcriptChunks } from "@/core/api/api";
import useToastStore from "@/shared/zustand/toastStore";
import { useMicVAD } from "@ricky0123/vad-react";
const AudioWaveform = ({ variant, isActive, className = "", onClick, disabled, initLoading }) => {
    const [barHeights, setBarHeights] = useState(Array(40).fill(4));
    const color = variant === "microphone" ? "#22C55E" : "#5928ED";
    const Icon = variant === "microphone"
        ? isActive ? Mic : MicOff
        : isActive ? Volume2 : VolumeOff;
    const audioContextRef = useRef(null);
    const analyserRef = useRef(null);
    const animationFrameRef = useRef(null);
    const streamRef = useRef(null);
    const startAudioAnalysis = async () => {
        try {
            if (!audioContextRef.current || audioContextRef.current.state === "closed") {
                audioContextRef.current = new AudioContext();
            }
            if (!streamRef.current) {
                streamRef.current = await navigator.mediaDevices.getUserMedia({ audio: true });
            }
            if (!analyserRef.current) {
                analyserRef.current = audioContextRef.current.createAnalyser();
                analyserRef.current.fftSize = 256;
                const source = audioContextRef.current.createMediaStreamSource(streamRef.current);
                source.connect(analyserRef.current);
            }
            analyzeAudio();
        }
        catch (error) {
            console.error("Error starting audio analysis:", error);
        }
    };
    const analyzeAudio = () => {
        if (!analyserRef.current || !isActive)
            return;
        const bufferLength = analyserRef.current.frequencyBinCount;
        const dataArray = new Uint8Array(bufferLength);
        analyserRef.current.getByteTimeDomainData(dataArray);
        // Calculate RMS (volume level)
        let sum = 0;
        for (let i = 0; i < bufferLength; i++) {
            const sample = (dataArray[i] / 128) - 1; // Normalize to -1 to 1
            sum += sample * sample;
        }
        const rms = Math.sqrt(sum / bufferLength);
        // console.log("RMS Volume:", rms); 
        const newHeights = Array(40).fill(0).map((_, i) => {
            const baseHeight = Math.abs(Math.sin(i * 0.5) * 32); // Increased base to 32
            if (rms < 0.02) { // rms threshold = 0.02
                return 5; // Flat 5px when silent
            }
            else {
                // Exaggerated scaling when speaking
                const volumeScale = Math.min(1, rms / 0.3); // Lower cap to 0.3 for more sensitivity
                return Math.min(14, baseHeight * (1 + volumeScale * 3));
            }
        });
        setBarHeights(newHeights);
        animationFrameRef.current = requestAnimationFrame(analyzeAudio);
    };
    useEffect(() => {
        if (isActive && variant === "microphone") {
            startAudioAnalysis();
        }
        else {
            // Reset barHeights and clean up when not active
            setBarHeights(Array(40).fill(4)); // Reset to initial state
            if (animationFrameRef.current) {
                cancelAnimationFrame(animationFrameRef.current);
                animationFrameRef.current = null;
            }
            if (streamRef.current) {
                streamRef.current.getTracks().forEach(track => track.stop());
                streamRef.current = null;
            }
            if (audioContextRef.current && audioContextRef.current.state !== "closed") {
                audioContextRef.current.close();
                audioContextRef.current = null;
            }
            analyserRef.current = null;
        }
        return () => {
            // Cleanup on unmount
            if (animationFrameRef.current) {
                cancelAnimationFrame(animationFrameRef.current);
                animationFrameRef.current = null;
            }
            if (streamRef.current) {
                streamRef.current.getTracks().forEach(track => track.stop());
                streamRef.current = null;
            }
            if (audioContextRef.current && audioContextRef.current.state !== "closed") {
                audioContextRef.current.close();
                audioContextRef.current = null;
            }
            analyserRef.current = null;
        };
    }, [isActive, variant]);
    return (_jsxs("div", { className: `relative h-12 z-50 w-1/2 overflow-y-visible overflow-x-hidden flex items-center ${className}`, children: [_jsx("div", { className: "w-full flex items-center justify-between h-8 gap-x-1", children: barHeights.map((height, i) => (_jsx("div", { className: "flex-shrink-0 !w-[4px] rounded-full transition-all duration-100", style: {
                        backgroundColor: color,
                        height: initLoading || !isActive ? "4px" : `${height}px`,
                        opacity: initLoading || !isActive ? 0.5 : isActive ? 1 : 0.2,
                    } }, i))) }), _jsx("button", { onClick: initLoading ? () => { } : onClick, "aria-label": `${variant} button`, className: `absolute left-1/2 top-1/2 transform -translate-x-1/2 -translate-y-1/2 
                   w-8 h-8 rounded-full flex items-center justify-center 
                   transition-all duration-200 ${initLoading ? "pointer-events-none" : disabled ? "cursor-not-allowed" : "cursor-pointer"}`, style: { backgroundColor: "white", border: `1px solid ${color}` }, children: _jsx(Icon, { color: color, className: "w-5 h-5 text-white" }) })] }));
};
const AudioControls = ({ className = "", initLoading, setInput, handleSend }) => {
    const showToast = useToastStore((state) => state.showToast);
    const { audioState, setAudioState } = useAudioStore();
    const mediaRecorder = useRef(null);
    // console.log("mediaRecorder", mediaRecorder.current?.state);
    const audioChunks = useRef([]);
    const audioContext = useRef(null);
    const audioBuffer = useRef(null);
    const audioSource = useRef(null);
    const recordingIntervalRef = useRef(null);
    const notSpeakingTimeoutRef = useRef(null);
    const [vadEnabled, setVadEnabled] = useState(false);
    const transcriptionRef = useRef("");
    const stopUpdateInputRef = useRef(false);
    // Using userHasStopped from the audioState instead of local state
    // VAD detector setup
    const vad = useMicVAD({
        onSpeechEnd: (audio) => {
            if (vadEnabled && audioState.isRecording) {
                vadDetector(false);
            }
        },
        onSpeechStart: () => {
            if (vadEnabled && audioState.isRecording) {
                // Clear any existing timeout when user starts speaking again
                if (notSpeakingTimeoutRef.current) {
                    clearTimeout(notSpeakingTimeoutRef.current);
                    notSpeakingTimeoutRef.current = null;
                }
                setAudioState(prev => ({ ...prev, userHasStopped: false }));
            }
        }
    });
    // Function to handle voice activity detection
    const vadDetector = (isSpeaking) => {
        if (!isSpeaking && !audioState.userHasStopped) {
            // User has stopped speaking, wait for 2 seconds to confirm
            notSpeakingTimeoutRef.current = setTimeout(() => {
                // Final confirmation that user is not speaking
                setAudioState(prev => ({ ...prev, userHasStopped: true }));
                // // Stop recording and process the audio
                // if (recordingIntervalRef.current) {
                //   clearInterval(recordingIntervalRef.current);
                // }
                // if (mediaRecorder.current && mediaRecorder.current.state !== "inactive") {
                //   mediaRecorder.current.stop();
                // }
                // Process the complete audio that has been recorded
                const completeAudioBlob = new Blob(audioChunks.current, { type: "audio/wav" });
                // downloadBlob(completeAudioBlob, `audio-recording-test.wav`);
                // Log the complete recorded audio and transcribed text
                console.log("Complete recorded audio:", completeAudioBlob);
                console.log("complete Transcribed text:", transcriptionRef.current);
                // Update audioState with the completeAudioBlob
                setAudioState(prev => ({
                    ...prev,
                    originalAudio: completeAudioBlob, // Store the blob for dispatchMessage
                }));
                // Set the flag to prevent further input updates
                // stopUpdateInputRef.current = true;
                // console.log("triggering handleSend");
                handleSend(transcriptionRef.current);
                setInput(""); // this will rm thank you msgs
                // Stop all tracks
                // if (mediaRecorder.current) {
                //   const stream = mediaRecorder.current.stream;
                //   stream.getTracks().forEach((track) => track.stop());
                // }
                transcriptionRef.current = "";
                setAudioState((prev) => ({
                    ...prev,
                    // isRecording: false,
                    userHasStopped: false, // Reset userHasStopped
                    originalAudio: completeAudioBlob
                }));
                //Reset audioChunks to start fresh for the next segment
                audioChunks.current = [];
                // Reset VAD state
                // setVadEnabled(false);
            }, 1000); // Wait for 1 second to confirm user has stopped speaking
        }
    };
    const hasMinimumVolume = async (audioBlob, threshold = 0.02) => {
        if (!audioContext.current || audioContext.current.state === "closed") {
            audioContext.current = new AudioContext();
        }
        try {
            // Convert blob to audio buffer
            const arrayBuffer = await audioBlob.arrayBuffer();
            const audioBuffer = await audioContext.current.decodeAudioData(arrayBuffer);
            // Get audio data from the first channel
            const audioData = audioBuffer.getChannelData(0);
            // Calculate RMS (Root Mean Square) to determine volume
            let sum = 0;
            for (let i = 0; i < audioData.length; i++) {
                sum += audioData[i] * audioData[i];
            }
            const rms = Math.sqrt(sum / audioData.length);
            console.log(`Audio RMS volume: ${rms}, threshold: ${threshold}`);
            return rms >= threshold;
        }
        catch (error) {
            console.error("Error analyzing audio volume:", error);
            // If there's an error, default to accepting the audio to avoid blocking valid audio
            return true;
        }
    };
    const startRecording = async () => {
        try {
            // Start the VAD
            try {
                console.log("Starting VAD...");
                vad.start();
                console.log("VAD started successfully");
            }
            catch (vadError) {
                console.error("VAD start error:", vadError);
                showToast("error", "Error", "Failed to start VAD");
                return;
            }
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            mediaRecorder.current = new MediaRecorder(stream);
            audioChunks.current = [];
            transcriptionRef.current = "";
            stopUpdateInputRef.current = false;
            mediaRecorder.current.ondataavailable = async (event) => {
                if (event.data.size > 0) {
                    const uniqueFilename = `chunk_${uuidv4()}.wav`;
                    audioChunks.current.push(event.data);
                    // sendAudioChunk(event.data, uniqueFilename);
                    // Check volume before sending for transcription
                    const hasVolume = await hasMinimumVolume(event.data);
                    if (hasVolume) {
                        console.log("Audio has sufficient volume, sending for transcription");
                        sendAudioChunk(event.data, uniqueFilename);
                    }
                    else {
                        console.log("Audio volume too low, skipping transcription");
                        // Still keep the chunk for the complete recording
                    }
                }
            };
            mediaRecorder.current.start();
            setAudioState((prev) => ({ ...prev, isRecording: true, transcription: "", userHasStopped: false }));
            // setAudioState(prev => ({ ...prev, userHasStopped: false }));
            setVadEnabled(true);
            // Chunk every 2 seconds
            recordingIntervalRef.current = setInterval(() => {
                if (mediaRecorder.current && mediaRecorder.current.state !== "inactive") {
                    mediaRecorder.current.stop();
                    mediaRecorder.current.start();
                }
            }, 2000);
        }
        catch (error) {
            console.error("Error starting recording:", error);
            // Reset state on error
            setAudioState((prev) => ({ ...prev, isRecording: false }));
            setVadEnabled(false);
            showToast("error", "Error", "Error accessing microphone. Please ensure microphone permissions are granted.");
        }
    };
    const stopRecording = async () => {
        if (mediaRecorder.current && audioState.isRecording) {
            if (recordingIntervalRef.current) {
                clearInterval(recordingIntervalRef.current);
            }
            if (notSpeakingTimeoutRef.current) {
                clearTimeout(notSpeakingTimeoutRef.current);
            }
            // Set the flag to prevent further input updates
            stopUpdateInputRef.current = true;
            mediaRecorder.current.stop();
            // Try to pause VAD but don't let errors stop execution
            try {
                vad.pause();
            }
            catch (e) {
                console.error("Error pausing VAD:", e);
                showToast("error", "Error", "Error pausing VAD");
            }
            // Create a blob from all collected chunks
            const completeAudioBlob = new Blob(audioChunks.current, { type: "audio/wav" });
            // await processAudioData(completeAudioBlob); // commenting for now as its not needed.
            // Stop all tracks
            const stream = mediaRecorder.current.stream;
            stream.getTracks().forEach((track) => track.stop());
            setAudioState((prev) => ({
                ...prev,
                isRecording: false,
                originalAudio: completeAudioBlob
            }));
            setVadEnabled(false);
            setAudioState(prev => ({ ...prev, userHasStopped: false }));
        }
    };
    const sendAudioChunk = async (audioBlob, filename) => {
        const formData = new FormData();
        formData.append("resource", audioBlob, filename);
        try {
            const { responseData, error } = await transcriptChunks(audioBlob, filename);
            if (error) {
                console.error("Error sending audio chunk:", error);
                showToast("error", "Error", error.errorMessage);
                return;
            }
            if (responseData && responseData.data && responseData.data.length > 0) {
                const newText = responseData.data[0].text;
                if (newText && newText.trim() !== "" && !stopUpdateInputRef.current) {
                    setAudioState((prev) => {
                        const updatedText = (prev.transcription + " " + newText).trim();
                        setInput(updatedText.trim());
                        transcriptionRef.current = updatedText.trim();
                        console.log("Transcription updated:", updatedText);
                        return { ...prev, transcription: updatedText };
                    });
                }
            }
            else {
                console.log("No transcription data returned from API");
            }
        }
        catch (error) {
            console.error("Error sending audio chunk:", error);
            showToast("error", "Error", "Error accessing microphone. Please ensure microphone permissions are granted.");
        }
    };
    const convertToBase64 = (blob) => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onloadend = () => {
                if (typeof reader.result === "string") {
                    resolve(reader.result);
                }
                else {
                    reject(new Error("Failed to convert to base64"));
                }
            };
            reader.onerror = reject;
            reader.readAsDataURL(blob);
        });
    };
    const convertToPCM16 = async (blob) => {
        if (!audioContext.current || audioContext.current.state === "closed") {
            audioContext.current = new AudioContext();
        }
        const arrayBuffer = await blob.arrayBuffer();
        const audioBuffer = await audioContext.current.decodeAudioData(arrayBuffer);
        const numberOfChannels = audioBuffer.numberOfChannels;
        const length = audioBuffer.length;
        const pcm16 = new Int16Array(length * numberOfChannels);
        for (let channel = 0; channel < numberOfChannels; channel++) {
            const channelData = audioBuffer.getChannelData(channel);
            for (let i = 0; i < length; i++) {
                const index = i * numberOfChannels + channel;
                pcm16[index] = Math.max(-32768, Math.min(32767, channelData[i] * 32768));
            }
        }
        return pcm16;
    };
    const processAudioData = async (audioBlob) => {
        try {
            // const [base64Data, pcm16Data] = await Promise.all([
            //   convertToBase64(audioBlob),
            //   convertToPCM16(audioBlob),
            // ]);
            if (!audioContext.current || audioContext.current.state === "closed") {
                audioContext.current = new AudioContext();
            }
            const arrayBuffer = await audioBlob.arrayBuffer();
            audioBuffer.current = await audioContext.current.decodeAudioData(arrayBuffer);
            // Log the original blob size
            console.log(`Original Blob Size: ${(audioBlob.size / 1024).toFixed(2)} KB`);
            // Log pcm16 size - each Int16 takes 2 bytes
            // console.log(`PCM16 Size: ${(pcm16Data.byteLength / 1024).toFixed(2)} KB`);
            // Log base64 size (approximately)
            // console.log(`Base64 String Length: ${base64Data.length} characters`);
            // console.log(`Base64 Size (approx): ${(base64Data.length / 1024).toFixed(2)} KB`);
            setAudioState((prev) => ({
                ...prev,
                originalAudio: audioBlob,
                // base64: base64Data,
                // pcm16: pcm16Data,
            }));
        }
        catch (error) {
            console.error("Error processing audio:", error);
            showToast("error", "Error", "Error processing audio data");
        }
    };
    const toggleRecording = () => {
        if (audioState.isRecording) {
            stopRecording();
        }
        else {
            startRecording();
        }
    };
    const playAudio = async () => {
        if (!audioContext.current || !audioBuffer.current || audioState.isPlaying) {
            return;
        }
        try {
            if (audioContext.current.state === "suspended") {
                await audioContext.current.resume();
            }
            audioSource.current = audioContext.current.createBufferSource();
            audioSource.current.buffer = audioBuffer.current;
            audioSource.current.connect(audioContext.current.destination);
            audioSource.current.onended = () => {
                setAudioState((prev) => ({ ...prev, isPlaying: false }));
            };
            setAudioState((prev) => ({ ...prev, isPlaying: true }));
            audioSource.current.start(0);
        }
        catch (error) {
            console.error("Error playing audio:", error);
            setAudioState((prev) => ({ ...prev, isPlaying: false }));
            showToast("error", "Error", "Error playing audio");
        }
    };
    // Cleanup function for MediaRecorder and VAD timeouts
    useEffect(() => {
        return () => {
            if (mediaRecorder.current && audioState.isRecording) {
                if (recordingIntervalRef.current) {
                    clearInterval(recordingIntervalRef.current);
                }
                if (notSpeakingTimeoutRef.current) {
                    clearTimeout(notSpeakingTimeoutRef.current);
                }
                mediaRecorder.current.stop();
                const stream = mediaRecorder.current.stream;
                stream.getTracks().forEach((track) => track.stop());
            }
        };
    }, [audioState.isRecording]);
    // Handle AudioContext cleanup on component unmount
    useEffect(() => {
        return () => {
            if (audioContext.current && audioContext.current.state !== "closed") {
                audioContext.current.close();
            }
        };
    }, []);
    const activeSpeaker = audioState.isRecording
        ? "microphone"
        : audioState.isPlaying
            ? "speaker"
            : "none";
    return (_jsx(_Fragment, { children: _jsxs("div", { className: `flex items-center w-full overflow-visible gap-x-2 ${className} ${initLoading ? "cursor-not-allowed" : ""}`, "data-pr-tooltip": initLoading ? "Loading..." : "", "data-pr-position": "top", children: [_jsx(AudioWaveform, { initLoading: initLoading, variant: "speaker", isActive: activeSpeaker === "speaker", onClick: playAudio, disabled: audioState.isPlaying || audioState.isRecording || !audioBuffer.current }), _jsx(AudioWaveform, { initLoading: initLoading, variant: "microphone", isActive: activeSpeaker === "microphone", onClick: toggleRecording, disabled: audioState.isPlaying }), initLoading && _jsx(Tooltip, { target: "[data-pr-tooltip]" })] }) }));
};
export default AudioControls;
